/**
 * GAMIFICATION HOOKS
 *
 * React hooks for accessing and managing gamification state
 */

import { useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot, getFirestore, collection, query, orderBy, limit, getDoc } from 'firebase/firestore';
import { firebaseApp } from '../firebase.js';
import gamificationService from '../services/gamificationService.js';

const db = getFirestore(firebaseApp);

/**
 * Hook to get and track user's gamification progress
 */
export function useUserProgress(userId) {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    // Real-time listener for user progress
    const unsubscribe = onSnapshot(
      doc(db, 'userProgress', userId),
      async (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setProgress({
            ...data,
            lastActivityDate: data.lastActivityDate?.toDate() || new Date(),
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date()
          });
        } else {
          // Initialize if doesn't exist
          try {
            const initialized = await gamificationService.initializeUserProgress(userId);
            setProgress(initialized);
          } catch (err) {
            setError(err);
          }
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error listening to user progress:', err);
        setError(err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  return { progress, loading, error };
}

/**
 * Hook to get user's achievements
 */
export function useUserAchievements(userId) {
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const loadAchievements = async () => {
      try {
        const userAchievements = await gamificationService.getUserAchievements(userId);
        setAchievements(userAchievements);
      } catch (error) {
        console.error('Error loading achievements:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAchievements();
  }, [userId]);

  return { achievements, loading };
}

/**
 * Hook to track user activity and update streaks
 */
export function useActivityTracker(userId) {
  const trackActivity = useCallback(
    async (activityType, userInfo = {}) => {
      if (!userId) return null;

      try {
        const result = await gamificationService.trackActivity(userId, activityType, userInfo);
        return result;
      } catch (error) {
        console.error('Error tracking activity:', error);
        return null;
      }
    },
    [userId]
  );

  return { trackActivity };
}

/**
 * Hook to unlock achievements
 */
export function useAchievementUnlocker(userId) {
  const [recentUnlocks, setRecentUnlocks] = useState([]);

  const unlockAchievement = useCallback(
    async (achievementId, metadata) => {
      if (!userId) return null;

      try {
        const result = await gamificationService.unlockAchievement(userId, achievementId, metadata);
        if (result.unlocked && result.achievement) {
          setRecentUnlocks((prev) => [...prev, result.achievement]);

          // Clear after 5 seconds
          setTimeout(() => {
            setRecentUnlocks((prev) => prev.filter((a) => a.id !== achievementId));
          }, 5000);
        }
        return result;
      } catch (error) {
        console.error('Error unlocking achievement:', error);
        return null;
      }
    },
    [userId]
  );

  return { unlockAchievement, recentUnlocks };
}

/**
 * Hook to get leaderboard data
 */
/**
 * Hook to get leaderboard data
 */
export function useLeaderboard(period = 'alltime', limitCount = 50) {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Real-time listener
    const q = query(
      collection(db, 'userProgress'),
      orderBy('totalPoints', 'desc'),
      limit(limitCount)
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      // 1. Map initial data from userProgress
      const initialData = snapshot.docs.map((doc) => {
        const d = doc.data();
        return {
          userId: doc.id,
          rawName: d.userName || d.username || d.profileName || d.name || d.displayName,
          email: d.userEmail || d.email,
          totalXP: d.totalXP || 0,
          currentLevel: d.currentLevel || 1,
          currentLevelName: d.currentLevelName || 'Explorer',
          totalPoints: d.totalPoints || 0,
          currentStreak: d.currentStreak || 0,
          isAnonymous: d.isAnonymous,
          avatar: d.avatar || null
        };
      });

      // 2. Enrich with userProfiles/jobseekers data
      const enrichedData = await Promise.all(initialData.map(async (entry) => {
        let finalName = entry.rawName;
        const emailPrefix = entry.email ? entry.email.split('@')[0] : '';

        // Detect if the name is a placeholder (User ID, Anonymous, matching email prefix, or missing)
        const isPlaceholder = !finalName ||
          finalName === 'User' ||
          finalName.toLowerCase().startsWith('user ') ||
          finalName.toLowerCase().startsWith('user_') ||
          (emailPrefix && finalName.toLowerCase() === emailPrefix.toLowerCase()) ||
          finalName.includes('Anonymous');

        if (isPlaceholder) {
          try {
            // Strategy 1: Try userProfiles (Job Seeker Profile)
            const profileRef = doc(db, 'userProfiles', entry.userId);
            const profileSnap = await getDoc(profileRef).catch(() => null);

            if (profileSnap && profileSnap.exists()) {
              const pData = profileSnap.data();
              const pName = pData.name || pData.resumeData?.personalInfo?.name;
              if (pName && pName !== 'User') {
                finalName = pName;
              }
            }

            // Strategy 2: If still placeholder, try jobseekers (Auth/Basic Info)
            if ((!finalName || finalName.toLowerCase().startsWith('user')) && !finalName.includes(' ')) {
              const seekerRef = doc(db, 'jobseekers', entry.userId);
              const seekerSnap = await getDoc(seekerRef).catch(() => null);
              if (seekerSnap && seekerSnap.exists()) {
                const sData = seekerSnap.data();
                const sName = sData.fullName || sData.name || sData.displayName;
                if (sName) {
                  finalName = sName;
                }
              }
            }
          } catch (err) {
            // silent fail
          }
        }

        // Fallback: If we still have nothing, construct a safe fallback
        const safeName = entry.isAnonymous
          ? `Anonymous`
          : (finalName && !finalName.toLowerCase().startsWith('user ') ? finalName : 'User');

        // Final cleanup: Ensure we don't display "User User" or similar artifacts
        const displayName = safeName.replace(/^User User/i, 'User');

        return {
          userId: entry.userId,
          userName: displayName,
          userEmail: entry.isAnonymous ? '' : (entry.email || ''),
          level: entry.currentLevel,
          levelName: entry.currentLevelName,
          levelEmoji: '🌱',
          points: entry.totalPoints,
          currentStreak: entry.currentStreak,
          rank: 0,
          avatar: entry.avatar
        };
      }));

      // 3. Set State
      setLeaderboard(enrichedData.map((item, index) => ({ ...item, rank: index + 1 })));
      setLoading(false);

    }, (error) => {
      console.error("Error listening to leaderboard:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [period, limitCount]);

  return { leaderboard, loading };
}

/**
 * Hook to get user's rank
 */
export function useUserRank(userId) {
  const [rank, setRank] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const loadRank = async () => {
      try {
        const rankData = await gamificationService.getUserRank(userId);
        setRank(rankData);
      } catch (error) {
        console.error('Error loading user rank:', error);
      } finally {
        setLoading(false);
      }
    };

    loadRank();

    // Refresh every 10 minutes
    const interval = setInterval(loadRank, 10 * 60 * 1000);

    return () => clearInterval(interval);
  }, [userId]);

  return { rank, loading };
}

/**
 * Hook to check test achievements after test completion
 */
export function useTestAchievements(userId) {
  const checkAchievements = useCallback(
    async (testScore, testTime) => {
      if (!userId) return [];

      try {
        const unlocked = await gamificationService.checkTestAchievements(userId, testScore, testTime);
        return unlocked;
      } catch (error) {
        console.error('Error checking test achievements:', error);
        return [];
      }
    },
    [userId]
  );

  return { checkAchievements };
}

